/**
 * MytenueCredits — service frontend pour interagir avec /credits/*
 *
 * Programme de fidélité Crédit Mytenue (art. L.521-1)
 *   - Lecture solde + historique + crédits expirant
 *   - Utilisation crédits (renew abo / upgrade / add-on)
 *   - Preview du gain potentiel sur un achat
 *
 * v1.12.846
 */

import { AWS_CONFIG } from '../config/aws_config';

const API = AWS_CONFIG.apiUrl;

function authHeaders(token) {
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

/**
 * GET /credits/balance — solde utilisateur connecté
 * Retourne { totalActive, totalPending, totalUsed, totalExpired, nearestExpiry }
 */
export async function getCreditBalance(token) {
  if (!token) return null;
  try {
    const res = await fetch(`${API}/credits/balance`, { headers: authHeaders(token) });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      active: Number(data.totalActive || 0),
      pending: Number(data.totalPending || 0),
      used: Number(data.totalUsed || 0),
      expired: Number(data.totalExpired || 0),
      nearestExpiry: data.nearestExpiry || null,
    };
  } catch (e) { return null; }
}

/**
 * GET /credits/history — historique transactions
 */
export async function getCreditHistory(token, limit = 50) {
  if (!token) return [];
  try {
    const res = await fetch(`${API}/credits/history?limit=${limit}`, { headers: authHeaders(token) });
    if (!res.ok) return [];
    const data = await res.json();
    return data.transactions || [];
  } catch (e) { return []; }
}

/**
 * GET /credits/expiring?days=30
 */
export async function getExpiringCredits(token, withinDays = 30) {
  if (!token) return { credits: [], totalExpiring: 0 };
  try {
    const res = await fetch(`${API}/credits/expiring?days=${withinDays}`, { headers: authHeaders(token) });
    if (!res.ok) return { credits: [], totalExpiring: 0 };
    return await res.json();
  } catch (e) { return { credits: [], totalExpiring: 0 }; }
}

/**
 * POST /credits/use — utiliser des crédits
 * @param {string} token JWT
 * @param {object} usage { type, itemId, itemName, amountUsed }
 *   type: 'subscription_renewal' | 'tier_upgrade' | 'addon_purchase'
 */
export async function useCredits(token, usage) {
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API}/credits/use`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(usage),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data; // { success, transactionId, newBalance }
}

/**
 * POST /credits/calculate-preview — preview du gain pour un achat
 */
export async function previewCreditEarn(purchaseAmount, userTier, token) {
  try {
    const res = await fetch(`${API}/credits/calculate-preview`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ purchaseAmount, userTier }),
    });
    if (!res.ok) return null;
    return await res.json(); // { credit, rate, max, tier }
  } catch (e) { return null; }
}

/**
 * Helper : déduit le montant max applicable depuis un solde credits
 *   sur un prix d'achat (peut pas dépasser le prix lui-même)
 */
export function maxApplicableCredits(activeBalance, purchasePrice) {
  return Math.min(Number(activeBalance || 0), Number(purchasePrice || 0));
}

/**
 * POST /credits/redeem — ÉCHANGE INTÉGRAL (100% crédit, AUCUN paiement Apple/Google)
 * Récompense fidélité L.521-1 : le backend octroie directement le consommable.
 * @param {string} token JWT
 * @param {string} rewardId  redeem_tryons_3 | redeem_tryons_10 | redeem_tryons_25 |
 *                           redeem_scans_50 | redeem_scans_200 | redeem_boost_50
 * @param {string} clientToken  clé d'idempotence (uuid généré côté client)
 * @returns { success, rewardId, addonId, costEur, granted:{quantity,type,ttlDays}, balance }
 */
export async function redeemCredit(token, rewardId, clientToken) {
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API}/credits/redeem`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ rewardId, clientToken }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.code = data.error;
    err.required = data.required;
    err.active = data.active;
    throw err;
  }
  return data;
}

// Map id add-on (config ADDONS) → rewardId échangeable par crédit (Phase 1, consommables)
export const ADDON_TO_REWARD = {
  addon_tryons_3:  'redeem_tryons_3',
  addon_tryons_10: 'redeem_tryons_10',
  addon_tryons_25: 'redeem_tryons_25',
  addon_scans_50:  'redeem_scans_50',
  addon_scans_200: 'redeem_scans_200',
  addon_wardrobe_boost_50: 'redeem_boost_50',
};
